import { useEffect, useRef, useState, useMemo } from "react";

/**
 * DEV-ONLY: Keeps a stable ref value across Vite HMR remounts.
 * Never runs in production. Zero risk of leaking dev state.
 *
 * @param {string} key - unique key for HMR persistence
 * @param {T} initialValue - initial value for fresh starts
 * @returns {React.MutableRefObject<T>}
 */
export function useHMRStableRef<T>(key: string, initialValue: T): React.MutableRefObject<T> {
  // Always call hooks unconditionally - React rules require this
  const isHMR = import.meta.hot ?? false;
  
  // Read from sessionStorage if present (only in dev/HMR mode)
  let stored: string | null = null;
  if (isHMR) {
    try {
      stored = sessionStorage.getItem(key);
    } catch {
      // Ignore storage errors
    }
  }
  
  const ref = useRef<T>(stored ? JSON.parse(stored) : initialValue);

  // Persist on every change (only in dev/HMR mode)
  useEffect(() => {
    if (isHMR) {
      try {
        sessionStorage.setItem(key, JSON.stringify(ref.current));
      } catch {
        // Ignore storage errors
      }
    }
  }, [key, isHMR]);

  return ref;
}

/**
 * DEV-ONLY: Keeps state stable across Vite HMR refreshes.
 * Production behaves exactly like normal useState.
 *
 * @param {string} key - unique storage key
 * @param {T | () => T} initialValue - same semantics as useState
 * @returns [T, (T) => void]
 */
export function useHMRStableState<T>(
  key: string,
  initialValue: T | (() => T)
): [T, (value: T | ((prev: T) => T)) => void] {
  // Always call hooks unconditionally - React rules require this
  const isHMR = import.meta.hot ?? false;
  
  // Read from sessionStorage if present (only in dev/HMR mode)
  let stored: string | null = null;
  if (isHMR) {
    try {
      stored = sessionStorage.getItem(key);
    } catch {
      // Ignore storage errors
    }
  }
  
  const initial =
    stored !== null
      ? JSON.parse(stored)
      : typeof initialValue === "function"
      ? (initialValue as () => T)()
      : initialValue;

  const [value, setValue] = useState<T>(initial);

  // Persist on every change (only in dev/HMR mode)
  useEffect(() => {
    if (isHMR) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Ignore storage errors
      }
    }
  }, [key, value, isHMR]);

  return [value, setValue];
}

/**
 * DEV-ONLY: Keeps memoized value stable across Vite HMR remounts.
 * Production behaves exactly like normal useMemo.
 *
 * @param {string} key
 * @param {() => T} computeFn
 * @returns {T}
 */
export function useHMRStableMemo<T>(key: string, computeFn: () => T): T {
  // Always call hooks unconditionally - React rules require this
  const isHMR = import.meta.hot ?? false;
  
  // Read from sessionStorage if present (only in dev/HMR mode)
  let stored: string | null = null;
  if (isHMR) {
    try {
      stored = sessionStorage.getItem(key);
    } catch {
      // Ignore storage errors
    }
  }

  const computedValue = useMemo(() => computeFn(), [computeFn]);
  const ref = useRef<T>(
    stored !== null ? JSON.parse(stored) : computedValue
  );

  // Update ref when computed value changes (only in dev/HMR mode)
  useEffect(() => {
    if (isHMR) {
      ref.current = computedValue;
      try {
        sessionStorage.setItem(key, JSON.stringify(ref.current));
      } catch {
        // Ignore storage errors
      }
    }
  }, [key, computedValue, isHMR]);

  return ref.current;
}

